import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import IndividualAffordabilityFields from '../../loan-application/steps/IndividualAffordabilityFields';
import { updateFinancialDetails } from '../../../services/loanService';
import { affordabilityError, capturedAffordability } from '../../../services/individualAffordability';
import './IndividualCapturedFinancials.css';

/** Application-owned declarations, including missing fields on pending older applications. */
export default function IndividualCapturedFinancials({ application, onSaved, inline = false, expensesOnly = false }: {
  application: any; onSaved: () => void; inline?: boolean; expensesOnly?: boolean;
}) {
  const f = application.financialDetails || {};
  const methods = useForm({ defaultValues: {
    individual: { monthlyNetIncome: f.income?.netIncome, monthlyLivingExpenses: f.livingExpenses },
    financial: { ...f, affordabilityCaptured: f.livingExpenses != null && f.maintenance != null }
  } });
  const [open, setOpen] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  return <div className="individual-captured-financials mt-3">
    {!inline && <button type="button" className="btn btn-outline-secondary" disabled={busy} aria-expanded={open}
      onClick={() => { setError(''); setOpen(value => !value); }}>Update captured financial figures</button>}
    {(inline || open) && <FormProvider {...methods}><div className="border rounded p-3 mt-2">
      <p>{inline ? 'Correct the saved figures only where supporting evidence differs. Totals update automatically.'
        : expensesOnly ? 'Save changed expense items before calculating. Their totals are calculated automatically.'
        : 'Save the applicant’s declarations separately from verified figures. Saving recalculates preliminary affordability and invalidates the analyst recommendation.'}</p>
      <fieldset disabled={busy}>
      <IndividualAffordabilityFields includeIncome={!expensesOnly} />
      <button type="button" className="btn btn-primary mt-3" disabled={busy} onClick={async () => {
        const values = methods.getValues(), figures = capturedAffordability(values);
        if (Object.values(figures).some(value => value == null || !Number.isFinite(value) || value < 0)) {
          setError('Complete each figure, entering zero explicitly where none applies.'); return;
        }
        setBusy(true); setError('');
        try {
          const { affordabilityCaptured, ...data } = values.financial;
          await updateFinancialDetails(application.id, data);
          setOpen(false); onSaved();
        } catch (failure) { setError(affordabilityError(failure)); }
        finally { setBusy(false); }
      }}>{busy ? 'Saving figures…' : expensesOnly ? 'Save changed expenses' : inline ? 'Save corrections' : 'Save captured figures and recalculate'}</button>
      </fieldset>
      {error && <p role="alert" className="text-danger">{error}</p>}
    </div></FormProvider>}
  </div>;
}
